import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import type { GalleryWithPhotos } from '@/lib/supabase'
import { GalleryDataAccess } from '@/features/gallery/api/galleryDataAccess'

/**
 * Fetch approved galleries for social media posting
 */
export function useApprovedGalleries() {
    return useQuery<GalleryWithPhotos[]>({
        queryKey: ['approved-galleries'],
        queryFn: async () => {
            try {
                const { data: galleries, error } = await supabase
                    .from('galleries')
                    .select('id, title, description, franchise_region, review_status, is_public, created_on, cover_image')
                    .eq('review_status', 'approved')
                    .eq('is_public', true)
                    .order('created_on', { ascending: false })

                if (error) {
                    console.error('Error fetching galleries:', error)
                    throw error
                }

                if (!galleries || galleries.length === 0) {
                    console.log('No approved galleries found')
                    return []
                }

                const photosByGalleryId = await GalleryDataAccess.getGalleryPhotosByGalleryIds(
                    galleries.map((gallery) => gallery.id)
                )

                const galleriesWithPhotos: GalleryWithPhotos[] = galleries.map((gallery) => ({
                    ...gallery,
                    gallery_photos: photosByGalleryId.get(gallery.id) ?? []
                } as GalleryWithPhotos))

                console.log(`✅ Successfully fetched ${galleriesWithPhotos.length} approved galleries`)
                return galleriesWithPhotos

            } catch (error) {
                console.error('Error in useApprovedGalleries:', error)
                throw error
            }
        },
        retry: (failureCount, error) => {
            if (failureCount >= 2) return false
            if (error?.message?.includes('JWT') || error?.message?.includes('permission')) return false
            return true
        },
        retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
        staleTime: 5 * 60 * 1000,
        gcTime: 10 * 60 * 1000
    })
}

/**
 * Fetch all galleries regardless of approval status
 */
export function useAllGalleries(options?: { enabled?: boolean }) {
    return useQuery<GalleryWithPhotos[]>({
        queryKey: ['all-galleries'],
        queryFn: async () => {
            try {
                const { data: galleries, error } = await supabase
                    .from('galleries')
                    .select('id, title, description, franchise_region, review_status, is_public, created_on, cover_image')
                    .order('created_on', { ascending: false })

                if (error) {
                    console.error('Error fetching all galleries:', error)
                    throw error
                }

                if (!galleries || galleries.length === 0) {
                    console.log('No galleries found')
                    return []
                }

                const photosByGalleryId = await GalleryDataAccess.getGalleryPhotosByGalleryIds(
                    galleries.map((gallery) => gallery.id)
                )

                const galleriesWithPhotos: GalleryWithPhotos[] = galleries.map((gallery) => ({
                    ...gallery,
                    gallery_photos: photosByGalleryId.get(gallery.id) ?? []
                } as GalleryWithPhotos))

                console.log(`✅ Successfully fetched ${galleriesWithPhotos.length} total galleries`)
                return galleriesWithPhotos

            } catch (error) {
                console.error('Error in useAllGalleries:', error)
                throw error
            }
        },
        retry: (failureCount, error) => {
            if (failureCount >= 2) return false
            if (error?.message?.includes('JWT') || error?.message?.includes('permission')) return false
            return true
        },
        retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
        staleTime: 5 * 60 * 1000,
        gcTime: 10 * 60 * 1000,
        enabled: options?.enabled ?? true,
    })
}
